import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BoltIcon from "@mui/icons-material/Bolt";
import DatasetOutlinedIcon from "@mui/icons-material/DatasetOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { Folder, File } from "lucide-react";

import { Comments } from "@/shared/components/comments";
import { RobotoLink } from "@/shared/components/RobotoLink";
import {
  commentEntityRoute,
  type CommentEntityType,
} from "@/shared/domain/comments";
import type { UIComment } from "@/shared/domain/comments/hooks/useUIComment";
import { Goto } from "@/shared/navigation/Goto";

import styles from "./CommentHeader.module.css";

interface CommentHeaderProps {
  /** Comment to display */
  comment: UIComment;
}

export function CommentHeader({ comment }: CommentHeaderProps) {
  const EntityIcon = getEntityIcon(comment.entity_type);
  const entityLink = commentEntityRoute(comment);

  // Fall back to the user ID if we don't have a display name
  const displayName = comment.creatorDisplayName ?? comment.created_by;

  return (
    <div className={styles.userInfo}>
      {comment.isSystemUser ? (
        <span className={styles.userName}>{displayName}</span>
      ) : (
        <RobotoLink
          to={Goto.createUserRoute(comment.created_by)}
          className={styles.userName}
        >
          {displayName}
        </RobotoLink>
      )}
      <span className={styles.separator}>•</span>
      <Comments.Time
        timestamp={comment.modified}
        className={styles.timestamp}
      />
      <span className={styles.separator}>•</span>
      <RobotoLink to={entityLink} className={styles.entityInfo}>
        <EntityIcon className={styles.entityIcon} />
        <span className={styles.entityId}>{comment.entity_id}</span>
      </RobotoLink>
    </div>
  );
}

/**
 * Returns the appropriate icon component for a given entity type
 */
function getEntityIcon(entityType: CommentEntityType) {
  switch (entityType) {
    case "dataset":
      return Folder;
    case "file":
      return File;
    case "action":
      return BoltIcon;
    case "collection":
      return DatasetOutlinedIcon;
    case "invocation":
      return AutoAwesomeIcon;
    case "trigger":
      return ScheduleIcon;
    default:
      return Folder;
  }
}
